import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  useImageLoadState,
  prefetchImage,
} from "../hooks/useImageLoadState";
import useViewport from "../hooks/useViewport";

const PHOTO_RATIO = 3 / 4; // All note photos are cropped to 3:4 at upload time

// Simple lightbox: stable 3:4 frame; shows cached images instantly;
// next-only prefetch; delayed spinner; optional placeholder.
export default function PhotoLightbox({
  open,
  images = [], // [{src, width?, height?, placeholder?}]
  index = 0,
  onIndexChange,
  onClose,
  maxVWFrac = 0.92,
  maxVHFrac = 0.92,
  spinnerDelayMs = 150,
}) {
  const { vw, vh } = useViewport();

  const boxDims = useMemo(() => {
    const maxW = vw * maxVWFrac;
    const maxH = vh * maxVHFrac;
    let w = maxW;
    let h = w / PHOTO_RATIO;
    if (h > maxH) {
      h = maxH;
      w = h * PHOTO_RATIO;
    }
    return { width: Math.floor(w), height: Math.floor(h) };
  }, [vw, vh, maxVWFrac, maxVHFrac]);

  const safeIndex = useMemo(
    () => (images.length ? Math.min(Math.max(index, 0), images.length - 1) : 0),
    [index, images.length]
  );
  const current = images[safeIndex];
  const currentSrc = current?.src;

  const {
    phase: curPhase,
    placeholder: generatedPlaceholder,
  } = useImageLoadState(currentSrc);

  // Delayed spinner logic
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    if (!currentSrc) {
      setShowSpinner(false);
      return;
    }
    if (curPhase === "loaded" || curPhase === "error") {
      setShowSpinner(false);
      return;
    }
    const t = setTimeout(() => setShowSpinner(true), spinnerDelayMs);
    return () => clearTimeout(t);
  }, [currentSrc, curPhase, spinnerDelayMs]);

  // Prefetch next AFTER current loads
  useEffect(() => {
    if (curPhase !== "loaded") return;
    const next = images[safeIndex + 1];
    if (next?.src) prefetchImage(next.src);
  }, [curPhase, images, safeIndex]);

  // Body scroll lock (minimal)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Handle Escape key to close lightbox
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onClose]);

  // Swipe gesture handling
  const touchStartRef = useRef(null);
  const touchDeltaRef = useRef(0);
  const SWIPE_THRESHOLD = 50;

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
    touchDeltaRef.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartRef.current === null) return;
    touchDeltaRef.current = e.touches[0].clientX - touchStartRef.current;
  };

  const handleTouchEnd = () => {
    const delta = touchDeltaRef.current;
    if (delta > SWIPE_THRESHOLD && safeIndex > 0) {
      onIndexChange?.(safeIndex - 1);
    } else if (delta < -SWIPE_THRESHOLD && safeIndex < images.length - 1) {
      onIndexChange?.(safeIndex + 1);
    }
    touchStartRef.current = null;
    touchDeltaRef.current = 0;
  };

  if (!open) return null;
  const canPrev = safeIndex > 0;
  const canNext = safeIndex < images.length - 1;
  const placeholder = current?.placeholder || generatedPlaceholder;

  const portal = (
    <div className="fixed inset-0 z-[80]" style={{ touchAction: "none" }}>
      <div className="absolute inset-0 bg-black/80" />
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 z-20 h-10 w-10 rounded-full bg-surface-elevated flex items-center justify-center text-text-secondary shadow ring-1 ring-border-strong hover:bg-surface-muted"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
      {canPrev && (
        <button
          onClick={() => onIndexChange?.(safeIndex - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-surface-elevated flex items-center justify-center text-text-secondary shadow hover:bg-surface-muted"
          aria-label="Previous"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
      )}
      {canNext && (
        <button
          onClick={() => onIndexChange?.(safeIndex + 1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-surface-elevated flex items-center justify-center text-text-secondary shadow hover:bg-surface-muted"
          aria-label="Next"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      )}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 px-4 py-1 rounded-full bg-surface-elevated text-text-primary text-xs font-medium shadow ring-1 ring-border-strong">
          {safeIndex + 1} / {images.length}
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center p-8" onClick={onClose}>
        <div
          className="relative bg-black/30 rounded-xl overflow-hidden flex items-center justify-center"
          style={{ width: boxDims.width, height: boxDims.height, touchAction: "none" }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Placeholder (only if not loaded and available) */}
          {placeholder && curPhase !== "loaded" && (
            <img
              src={placeholder}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                imageRendering: "pixelated",
                filter: "blur(4px) brightness(1.05)",
              }}
            />
          )}
          {/* High-res image (always mounted when src exists) */}
          {currentSrc && (
            <img
              src={currentSrc}
              alt={current?.alt || ""}
              className="absolute inset-0 w-full h-full object-cover select-none"
              draggable={false}
            />
          )}
          {/* Spinner (delayed) */}
          {showSpinner && curPhase === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            </div>
          )}
          {/* Error state */}
          {curPhase === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/85 text-sm">
              <div>Failed to load image</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  return createPortal(portal, document.body);
}
